<template lang="pug">
u-main
  u-container
    u-page
      u-page-header(
        title="Privacy Policy"
        headline="Last updated: May 2026"
      )
      u-page-body
        article.flex.flex-col.gap-8.text-sm.text-muted.leading-relaxed
          p.text-base.text-default
            | This Privacy Policy explains how Unattended Oy (“Unattended Company,” “we,” “us,” or “our”) collects, uses, discloses, and safeguards information when you visit
            |
            u-link(
              href="https://unattended.company"
              external
            ) unattended.company
            |
            | or otherwise interact with our company online presence. It also describes general privacy practices for mobile applications we publish, unless a specific product provides a more detailed policy. By using this website or our apps, you agree to the practices described below.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 1. Who We Are
            p
              | The data controller for this website and our published apps is Unattended Oy, a company registered in Finland under Business ID 3622216-6. For privacy questions or requests, contact us at
              |
              u-link(href="mailto:frank@unattended.company") frank@unattended.company
              | .

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 2. Scope
            p
              | This policy applies to unattended.company and related pages operated by Unattended Oy. Individual apps and services may collect additional information and are governed by their own privacy policies where those policies provide more detail for that product.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 3. Our Apps
            p
              | When you use our mobile applications, product-specific privacy practices may apply. For example, Poemly is described in the
              |
              u-link(
                href="https://poemly.app/privacy"
                target="_blank"
                external
              ) Poemly Privacy Policy
              |
              | on poemly.app, including information about accounts, in-app content, subscriptions, and device data. Please read the applicable product policy before using an app.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 4. Information We Collect
            p Depending on how you interact with us, we may collect:
            ul(class="list-disc pl-5 flex flex-col gap-1.5")
              li
                strong.font-semibold.text-default Website and contact information.
                |
                | Information you provide when you contact us (such as your name, email address, and the content of your message) or when you subscribe to updates, if offered.
              li
                strong.font-semibold.text-default Technical and usage data.
                |
                | Information automatically collected when you visit the website, such as IP address, browser type, device type, referring pages, and approximate location derived from IP. We may use cookies or similar technologies as described in Section 12.
              li
                strong.font-semibold.text-default App-related information.
                |
                | If you use our apps, we may collect information described in the relevant product privacy policy, such as account identifiers, usage events, crash reports, and content you create in the app.
              li
                strong.font-semibold.text-default Communications.
                |
                | Messages you send us for support, legal notices, or other inquiries.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 5. How We Use Information
            p We use the information we collect to:
            ul(class="list-disc pl-5 flex flex-col gap-1.5")
              li Operate, maintain, and improve the website and our apps
              li Respond to your inquiries and provide support
              li Send transactional or service-related messages where appropriate
              li Monitor performance, diagnose technical problems, and prevent abuse or fraud
              li Comply with applicable legal obligations
              li Understand aggregate use of the website and improve our products

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 6. Legal Bases for Processing
            p
              | Where the GDPR or similar laws apply, we process your information on the basis of performance of a contract (to respond to your requests or provide an app you use), our legitimate interests (to operate and secure our services and improve them), your consent (for optional communications or non-essential cookies where required), and compliance with legal obligations.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 7. How We Share Information
            p
              | We do not sell your personal information. We share data only with vetted service providers that help us operate the website and our apps (for example, hosting, authentication, email delivery, analytics, and payment or subscription platforms used by our apps), and only to the extent necessary for them to perform their functions. We may also disclose information if required by law, to enforce our
              |
              u-link(to="/terms") Terms of Service
              | , or to protect the rights, safety, and property of Unattended Oy or others.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 8. Data Retention
            p
              | We retain personal information for as long as needed to fulfill the purposes described in this policy, unless a longer retention period is required or permitted by law. If you delete an account in one of our apps, we will handle deletion as described in the applicable product policy (for example, permanent removal within 30 days where stated, except records we must retain for legal, tax, or security reasons). Anonymized, aggregated data that cannot reasonably be linked back to you may be retained.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 9. Your Rights
            p
              | Depending on your jurisdiction, you may have the right to access, correct, export, or delete your personal data, restrict or object to certain processing, and withdraw consent at any time. To exercise these rights, contact
              |
              u-link(href="mailto:frank@unattended.company") frank@unattended.company
              | . You also have the right to lodge a complaint with your local data protection authority. In Finland, the supervisory authority is the Office of the Data Protection Ombudsman (
              u-link(
                href="https://tietosuoja.fi"
                target="_blank"
                external
              ) tietosuoja.fi
              | ).

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 10. Security
            p
              | We use appropriate technical and organizational measures, including encryption in transit and at rest where applicable, access controls, and routine security practices, to protect your information. No method of transmission or storage is perfectly secure, but we work to safeguard your data.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 11. Children's Privacy
            p
              | Our website and apps are not directed to children under 13 (or the equivalent minimum age in your jurisdiction). We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us and we will delete it.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 12. Cookies and Similar Technologies
            p
              | We may use cookies and similar technologies on the website to remember preferences, measure traffic, and improve performance. Where required by law, we will ask for your consent before using non-essential cookies. You can control cookies through your browser settings; disabling cookies may affect some site features.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 13. International Transfers
            p
              | Your information may be processed in countries other than your own, including countries outside the European Economic Area. Where required, we rely on appropriate safeguards such as Standard Contractual Clauses or other lawful transfer mechanisms to protect your data.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 14. Changes to This Policy
            p
              | We may update this Privacy Policy from time to time. When we do, we will revise the “Last updated” date above and, where appropriate, provide notice on the website or by email. Your continued use of the website or our apps after changes take effect constitutes your acceptance of the revised policy.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 15. Contact Us
            p
              | For any questions, requests, or concerns about this Privacy Policy or your personal data, contact Unattended Oy at
              |
              u-link(href="mailto:frank@unattended.company") frank@unattended.company
              | .
</template>

<script>
export default {
  name: 'PagePrivacy'
}
</script>
